import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  BarChart3, TrendingUp, DollarSign, Car, Eye, Heart, Calendar,
  Download, Users, MousePointerClick, RotateCcw,
} from 'lucide-react';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { useTranslation } from '../../i18n';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const localize = (lang, tr, es, en) => (lang === 'tr' ? tr : lang === 'es' ? es : en);

const KpiCard = ({ icon: Icon, color, label, value, testId }) => (
  <div
    className="p-5 rounded-lg shadow-sm border-l-4"
    style={{ background: 'var(--bg-primary)', borderLeftColor: color }}
    data-testid={testId}
  >
    <div className="flex items-center gap-3">
      <div className="p-3 rounded-lg" style={{ background: `${color}22` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide truncate" style={{ color: 'var(--text-secondary)' }}>
          {label}
        </p>
        <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{value}</p>
      </div>
    </div>
  </div>
);

const UnifiedDashboard = ({ lang }) => {
  const { t } = useTranslation(lang);
  const [perf, setPerf] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const [pRes, aRes] = await Promise.all([
        axios.get(`${API}/vehicles/admin/statistics`, { headers }),
        axios.get(`${API}/analytics/stats`, { headers }),
      ]);
      setPerf(pRes.data);
      setAnalytics(aRes.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error(t('perf.loadFailed') || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vehicles/admin/export-excel`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'vehicles_report.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success((t('admin.success') || 'Exported') + '!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(t('admin.error') || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleResetAnalytics = async () => {
    const msg = localize(lang,
      'Tüm ziyaret verileri kalıcı olarak silinecek. Emin misiniz?',
      '¿Está seguro? Se eliminarán TODOS los datos de analítica.',
      'This will permanently delete ALL analytics data. Are you sure?');
    if (!window.confirm(msg)) return;
    try {
      setResetting(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/analytics/reset`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(localize(lang, 'Analitik sıfırlandı', 'Analítica reiniciada', 'Analytics reset'));
      await fetchAll();
    } catch (error) {
      console.error('Reset failed:', error);
      toast.error(localize(lang, 'Sıfırlama başarısız', 'Error al reiniciar', 'Reset failed'));
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="unified-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'var(--accent-primary)' }} />
      </div>
    );
  }

  const trafficLabels = [t('admin.direct'), t('admin.google'), t('admin.socialMedia'), t('admin.other')];
  const trafficData = analytics ? [
    analytics.traffic_sources.direct,
    analytics.traffic_sources.google,
    analytics.traffic_sources.social,
    analytics.traffic_sources.other,
  ] : [0, 0, 0, 0];

  return (
    <div className="space-y-8" data-testid="unified-dashboard">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="text-3xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'Playfair Display' }}>
            {localize(lang, 'Panel & Analitik', 'Panel & Analítica', 'Insights & Analytics')}
          </h2>
          <p style={{ color: 'var(--text-secondary)' }} className="mt-1">
            {t('perf.subtitle')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={handleExportExcel} disabled={exporting} data-testid="export-excel-btn">
            <Download className="w-4 h-4 mr-2" />
            {exporting ? t('perf.exporting') : t('perf.exportExcel')}
          </Button>
          <Button
            variant="outline"
            onClick={handleResetAnalytics}
            disabled={resetting}
            data-testid="reset-analytics-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            {resetting
              ? localize(lang, 'Siliniyor...', 'Borrando...', 'Resetting...')
              : localize(lang, 'Analitiği Sıfırla', 'Reiniciar Analítica', 'Reset Analytics')}
          </Button>
        </div>
      </div>

      {/* KPI Cards — Inventory */}
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-secondary)' }}>
          {localize(lang, 'Envanter', 'Inventario', 'Inventory')}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <KpiCard icon={Car} color="#3b82f6" label={t('perf.totalVehicles')} value={perf?.vehicles?.total ?? 0} testId="kpi-total-vehicles" />
          <KpiCard icon={TrendingUp} color="#10b981" label={t('perf.available')} value={perf?.vehicles?.available ?? 0} testId="kpi-available" />
          <KpiCard icon={BarChart3} color="#8b5cf6" label={t('perf.sold')} value={perf?.vehicles?.sold ?? 0} testId="kpi-sold" />
          <KpiCard icon={DollarSign} color="#059669" label={t('perf.totalSoldValue')} value={`$${(perf?.sales?.total_sold_value ?? 0).toLocaleString()}`} testId="kpi-sold-value" />
          <KpiCard icon={DollarSign} color="#eab308" label={t('perf.avgPrice')} value={`$${Math.round(perf?.pricing?.average ?? 0).toLocaleString()}`} testId="kpi-avg-price" />
        </div>
      </div>

      {/* KPI Cards — Traffic */}
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-secondary)' }}>
          {localize(lang, 'Trafik', 'Tráfico', 'Traffic')}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <KpiCard icon={Eye} color="#3b82f6" label={t('admin.totalVisits')} value={analytics?.total_visits ?? 0} testId="kpi-total-visits" />
          <KpiCard icon={Users} color="#10b981" label={t('admin.uniqueVisitors')} value={analytics?.unique_visitors ?? 0} testId="kpi-unique-visitors" />
          <KpiCard icon={TrendingUp} color="#8b5cf6" label={t('admin.todayVisits')} value={analytics?.today_visits ?? 0} testId="kpi-today-visits" />
          <KpiCard icon={MousePointerClick} color="#f97316" label={t('admin.avgPageViews')} value={analytics?.avg_page_views ?? 0} testId="kpi-avg-page-views" />
        </div>
      </div>

      {/* Charts */}
      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="p-6 rounded-lg shadow-sm" style={{ background: 'var(--bg-primary)' }}>
            <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              {t('admin.trafficSources')}
            </h3>
            <div className="h-72">
              <Doughnut
                data={{
                  labels: trafficLabels,
                  datasets: [{
                    data: trafficData,
                    backgroundColor: [
                      'rgba(59, 130, 246, 0.8)',
                      'rgba(239, 68, 68, 0.8)',
                      'rgba(34, 197, 94, 0.8)',
                      'rgba(234, 179, 8, 0.8)',
                    ],
                  }],
                }}
                options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } }}
              />
            </div>
          </div>

          <div className="p-6 rounded-lg shadow-sm" style={{ background: 'var(--bg-primary)' }}>
            <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              {t('admin.popularPages')}
            </h3>
            <div className="h-72">
              <Bar
                data={{
                  labels: (analytics.popular_pages || []).map(p => p.page),
                  datasets: [{
                    label: t('admin.visits'),
                    data: (analytics.popular_pages || []).map(p => p.visits),
                    backgroundColor: 'rgba(59, 130, 246, 0.7)',
                  }],
                }}
                options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true } } }}
              />
            </div>
          </div>
        </div>
      )}

      {/* 7-Day Trend */}
      {analytics?.last_7_days && (
        <div className="p-6 rounded-lg shadow-sm" style={{ background: 'var(--bg-primary)' }}>
          <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            {t('admin.weeklyTrend')}
          </h3>
          <div className="h-72">
            <Line
              data={{
                labels: analytics.last_7_days.map(d => new Date(d.date).toLocaleDateString(lang === 'tr' ? 'tr-TR' : lang === 'es' ? 'es-ES' : 'en-US')),
                datasets: [{
                  label: t('admin.dailyVisits'),
                  data: analytics.last_7_days.map(d => d.visits),
                  borderColor: 'rgb(59, 130, 246)',
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  tension: 0.4,
                  fill: true,
                }],
              }}
              options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true } } }}
            />
          </div>
        </div>
      )}

      {/* Engagement + Pricing */}
      {perf && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="p-6 rounded-lg shadow-sm" style={{ background: 'var(--bg-primary)' }}>
            <div className="flex items-center gap-3 mb-4">
              <Calendar className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
              <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{t('perf.customerEngagement')}</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span style={{ color: 'var(--text-secondary)' }}>{t('perf.testDriveRequests')}</span>
                <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{perf.engagement.test_drives}</span>
              </div>
              <div className="flex justify-between items-center">
                <span style={{ color: 'var(--text-secondary)' }}>{t('perf.pendingTestDrives')}</span>
                <span className="font-semibold text-orange-600">{perf.engagement.pending_test_drives}</span>
              </div>
              <div className="flex justify-between items-center">
                <span style={{ color: 'var(--text-secondary)' }}>{t('perf.priceQuotes')}</span>
                <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{perf.engagement.price_quotes}</span>
              </div>
              <div className="flex justify-between items-center">
                <span style={{ color: 'var(--text-secondary)' }}>{t('perf.pendingQuotes')}</span>
                <span className="font-semibold text-orange-600">{perf.engagement.pending_quotes}</span>
              </div>
            </div>
          </div>

          <div className="p-6 rounded-lg shadow-sm" style={{ background: 'var(--bg-primary)' }}>
            <div className="flex items-center gap-3 mb-4">
              <DollarSign className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
              <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{t('perf.pricingStats')}</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span style={{ color: 'var(--text-secondary)' }}>{t('perf.avgPrice')}</span>
                <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>${Math.round(perf.pricing.average).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span style={{ color: 'var(--text-secondary)' }}>{t('perf.minPrice')}</span>
                <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>${Math.round(perf.pricing.minimum).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span style={{ color: 'var(--text-secondary)' }}>{t('perf.maxPrice')}</span>
                <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>${Math.round(perf.pricing.maximum).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span style={{ color: 'var(--text-secondary)' }}>{t('perf.featuredVehicles')}</span>
                <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{perf.vehicles.featured}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Most Viewed */}
      {perf?.top_vehicles?.most_viewed && (
        <div className="p-6 rounded-lg shadow-sm" style={{ background: 'var(--bg-primary)' }}>
          <div className="flex items-center gap-3 mb-4">
            <Eye className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
            <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{t('perf.mostViewed')}</h3>
          </div>
          <div className="space-y-3">
            {perf.top_vehicles.most_viewed.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)' }}>{t('perf.noData')}</p>
            ) : (
              perf.top_vehicles.most_viewed.map((vehicle, index) => (
                <div key={vehicle.id} className="flex items-center gap-4 p-3 rounded-lg" style={{ background: 'var(--bg-secondary)' }}>
                  <span className="font-bold text-lg" style={{ color: 'var(--text-secondary)' }}>#{index + 1}</span>
                  {vehicle.images?.[0] && (
                    <img src={vehicle.images[0]} alt={vehicle.title} className="w-16 h-16 object-cover rounded-lg" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                      {vehicle.title || `${vehicle.year} ${vehicle.brand} ${vehicle.model}`}
                    </p>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>${vehicle.price?.toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Eye className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
                    <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{vehicle.view_count}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Most Favorited */}
      {perf?.top_vehicles?.most_favorited && (
        <div className="p-6 rounded-lg shadow-sm" style={{ background: 'var(--bg-primary)' }}>
          <div className="flex items-center gap-3 mb-4">
            <Heart className="w-5 h-5" style={{ color: 'var(--accent-primary)' }} />
            <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{t('perf.mostFavorited')}</h3>
          </div>
          <div className="space-y-3">
            {perf.top_vehicles.most_favorited.length === 0 ? (
              <p style={{ color: 'var(--text-secondary)' }}>{t('perf.noData')}</p>
            ) : (
              perf.top_vehicles.most_favorited.map((vehicle, index) => (
                <div key={vehicle.id} className="flex items-center gap-4 p-3 rounded-lg" style={{ background: 'var(--bg-secondary)' }}>
                  <span className="font-bold text-lg" style={{ color: 'var(--text-secondary)' }}>#{index + 1}</span>
                  {vehicle.images?.[0] && (
                    <img src={vehicle.images[0]} alt={vehicle.title} className="w-16 h-16 object-cover rounded-lg" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                      {vehicle.title || `${vehicle.year} ${vehicle.brand} ${vehicle.model}`}
                    </p>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>${vehicle.price?.toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Heart className="w-4 h-4 text-red-500" />
                    <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{vehicle.favorite_count}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Referrers */}
      {analytics?.detailed_referrers && analytics.detailed_referrers.length > 0 && (
        <div className="rounded-lg shadow-sm overflow-hidden" style={{ background: 'var(--bg-primary)' }}>
          <div className="p-6">
            <h3 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>
              {t('admin.detailedReferrers')}
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>
                    {t('admin.referrer')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase" style={{ color: 'var(--text-secondary)' }}>
                    {t('admin.visits')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
                {analytics.detailed_referrers.map((ref, idx) => (
                  <tr key={idx}>
                    <td className="px-6 py-4" style={{ color: 'var(--text-primary)' }}>{ref.referrer}</td>
                    <td className="px-6 py-4 font-semibold" style={{ color: 'var(--text-primary)' }}>{ref.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedDashboard;
